/**
 * Override of @foundation/components/admin/AdminSidebar
 * Super Admin Portal — visible to platform admins only.
 * Shows: Subscriptions, Addons, User Management, Roles, Base Templates, Assign Bases.
 * Policy Templates and AI Skills are in the separate Org Admin portal (/org-admin).
 */
import {
  Activity,
  BellRing,
  CreditCard,
  Database,
  KeyRound,
  Layers,
  MessageSquare,
  Package,
  ShieldCheck,
  Sparkles,
  Users,
} from 'lucide-react';
import React from 'react';
import { WorkspaceSwitcher } from '@header/WorkspaceSwitcher';
import { cn } from '@foundation/lib/utils';
import { Badge } from '@foundation/components/ui/badge';

export type AdminTab =
  | 'subscriptions'
  | 'addon-packs'
  | 'users'
  | 'roles'
  | 'teable-config'
  | 'assign-bases'
  | 'global-integrations'
  | 'policy-templates'
  | 'ai-skills'
  | 'feedback'
  | 'document-register-signups'
  | 'activity-log';

interface AdminSidebarProps {
  activeTab: AdminTab;
  onTabChange: (tab: AdminTab) => void;
  pendingUsersCount?: number;
}

export const AdminSidebar: React.FC<AdminSidebarProps> = ({
  activeTab,
  onTabChange,
  pendingUsersCount = 0,
}) => {
  const tabs = [
    {
      id: 'subscriptions' as AdminTab,
      label: 'Subscriptions',
      icon: CreditCard,
      description: 'Manage plan tiers and pricing',
    },
    {
      id: 'addon-packs' as AdminTab,
      label: 'Addons',
      icon: Package,
      description: 'Manage purchasable addon packs',
    },
    {
      id: 'users' as AdminTab,
      label: 'User Management',
      icon: Users,
      description: 'Approve and manage users',
      badge: pendingUsersCount > 0 ? pendingUsersCount : undefined,
    },
    {
      id: 'roles' as AdminTab,
      label: 'Roles',
      icon: KeyRound,
      description: 'Create and manage roles',
    },
    {
      id: 'teable-config' as AdminTab,
      label: 'Base Templates',
      icon: Database,
      description: 'Manage base template configurations',
    },
    {
      id: 'assign-bases' as AdminTab,
      label: 'Assign Bases',
      icon: Layers,
      description: 'Manage base assignments',
    },
    {
      id: 'ai-skills' as AdminTab,
      label: 'AI Skills',
      icon: Sparkles,
      description: 'Configure AI generation skills',
    },
    {
      id: 'feedback' as AdminTab,
      label: 'Feedback',
      icon: MessageSquare,
      description: 'Policy generation ratings',
    },
    {
      id: 'document-register-signups' as AdminTab,
      label: 'Document Register Signups',
      icon: BellRing,
      description: 'Pro registers waitlist',
    },
    {
      id: 'activity-log' as AdminTab,
      label: 'Activity Log',
      icon: Activity,
      description: 'Policies created per organisation',
    },
  ];

  return (
    <div
      className='w-64 shrink-0 flex flex-col h-full relative overflow-hidden'
      style={{ background: '#132321', borderRight: '1px solid rgba(51,184,100,0.15)' }}
    >
      {/* Dot-grid texture */}
      <div
        className='absolute inset-0 pointer-events-none opacity-[0.03]'
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)',
          backgroundSize: '24px 24px',
        }}
      />
      {/* Top green accent line */}
      <div className='h-[2px] w-full flex-shrink-0' style={{ background: 'linear-gradient(90deg, #33b864, transparent)' }} />

      {/* Workspace switcher */}
      <div className='pt-3 relative z-10'>
        <WorkspaceSwitcher />
      </div>

      {/* Header */}
      <div className='px-5 py-4 relative z-10' style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <div className='flex items-center gap-2.5'>
          <div className='w-8 h-8 rounded-lg flex items-center justify-center' style={{ background: 'rgba(51,184,100,0.15)' }}>
            <ShieldCheck className='h-4 w-4' style={{ color: '#33b864' }} />
          </div>
          <div>
            <h2 className='text-sm font-bold' style={{ color: '#ffffff', letterSpacing: '-0.01em' }}>Admin</h2>
            <p className='text-[11px]' style={{ color: 'rgba(255,255,255,0.45)' }}>Platform management</p>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className='flex-1 overflow-y-auto p-2.5 space-y-0.5 relative z-10'>
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className='w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all duration-150 relative'
              style={{
                background: isActive ? 'rgba(51,184,100,0.14)' : 'transparent',
                color: isActive ? '#33b864' : 'rgba(255,255,255,0.72)',
              }}
              onMouseEnter={e => {
                if (!isActive) {
                  (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.07)';
                  (e.currentTarget as HTMLElement).style.color = '#ffffff';
                }
              }}
              onMouseLeave={e => {
                if (!isActive) {
                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                  (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.72)';
                }
              }}
            >
              {/* Active left pill */}
              {isActive && (
                <div
                  className='absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full'
                  style={{ background: '#33b864' }}
                />
              )}
              <Icon
                className='h-4 w-4 flex-shrink-0'
                style={{ color: isActive ? '#33b864' : 'rgba(255,255,255,0.5)' }}
              />
              <div className='flex-1 min-w-0'>
                <div className='text-sm font-medium truncate'>{tab.label}</div>
                <div className='text-[11px] truncate' style={{ color: isActive ? 'rgba(51,184,100,0.75)' : 'rgba(255,255,255,0.38)' }}>
                  {tab.description}
                </div>
              </div>
              {tab.badge && (
                <span
                  className='text-[10px] font-bold px-1.5 py-0.5 rounded-full'
                  style={{ background: '#33b864', color: '#ffffff' }}
                >
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
};
